import React, { useRef, useEffect, useState } from 'react';
import * as faceapi from 'face-api.js';
import Webcam from 'react-webcam';
import { loadModelsFromUri, areModelsLoaded } from '../services/ModelService';

const FaceAuth = ({ onAuthenticated, isLoggedOut }) => {
  const webcamRef = useRef(null);
  const canvasRef = useRef(null);
  const [isModelLoaded, setIsModelLoaded] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [registeredUser, setRegisteredUser] = useState(null);
  const [scanningMessage, setScanningMessage] = useState('Loading face recognition models...');
  const [webcamError, setWebcamError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [retryCount, setRetryCount] = useState(0);
  const [username, setUsername] = useState('');
  const [showUsernameInput, setShowUsernameInput] = useState(false);
  
  // Password protection
  const [showPasswordInput, setShowPasswordInput] = useState(false);
  const [password, setPassword] = useState('');
  const [isRegistrationMode, setIsRegistrationMode] = useState(false);
  const ADMIN_PASSWORD = "sonam8948"; // Hard-coded password as specified

  // Watch for direct logout signals from parent component
  useEffect(() => {
    if (isLoggedOut && isAuthenticated) {
      setIsAuthenticated(false);
      setScanningMessage('You have been logged out. Please authenticate again to use voice commands.');
    }
  }, [isLoggedOut]);

  // Check localStorage for auth status and listen for custom logout events
  useEffect(() => {
    // Function to handle logout
    const handleLogout = () => {
      if (isAuthenticated) {
        console.log('Logout event received in FaceAuth component');
        setIsAuthenticated(false);
        setScanningMessage('You have been logged out. Please authenticate again to use voice commands.');
      }
    };
    
    const checkAuthStatus = () => {
      const authStatus = localStorage.getItem('isAuthenticated');
      if (authStatus === 'false' && isAuthenticated) {
        handleLogout();
      }
    };
    
    checkAuthStatus();
    
    // Listen for custom userLogout event
    window.addEventListener('userLogout', handleLogout);
    
    // Set up an interval to periodically check auth status
    const interval = setInterval(checkAuthStatus, 1000);
    
    return () => {
      clearInterval(interval);
      window.removeEventListener('userLogout', handleLogout);
    };
  }, [isAuthenticated]);

  // Load face-api models
  useEffect(() => {
    const loadModels = async () => {
      try {
        setIsLoading(true);
        // Check if models are already loaded
        if (areModelsLoaded()) {
          setIsModelLoaded(true);
          setScanningMessage('Face models already loaded. Ready to authenticate.');
          setIsLoading(false);
          return;
        }
        
        // Load models using our service
        const result = await loadModelsFromUri();
        
        if (result.success) {
          setIsModelLoaded(true);
          setScanningMessage('Face models loaded. Ready to authenticate.');
        } else {
          if (retryCount < 2) {
            setScanningMessage(`Retrying model load (${retryCount + 1}/3)...`);
            setRetryCount(prev => prev + 1);
            // Retry after a delay
            setTimeout(loadModels, 2000);
            return;
          } else {
            setScanningMessage(`Error loading models: ${result.message}. Please refresh the page.`);
          }
        }
        
        setIsLoading(false);
      } catch (error) {
        console.error('Error loading models:', error);
        
        if (retryCount < 2) {
          setScanningMessage(`Error loading models. Retrying (${retryCount + 1}/3)...`);
          setRetryCount(prev => prev + 1);
          // Retry after a delay
          setTimeout(loadModels, 2000);
          return;
        } else {
          setScanningMessage('Error loading face recognition models. Please refresh the page.');
          setIsLoading(false);
        }
      }
    };

    loadModels();
  }, [retryCount]);

  // Handle webcam errors
  const handleWebcamError = (error) => {
    console.error('Webcam error:', error);
    setWebcamError(true);
    setScanningMessage('Camera access denied or not available. Please allow camera access and refresh the page.');
  };

  // Function to request admin password for registration
  const showAdminPasswordPrompt = () => {
    setPassword('');
    setShowPasswordInput(true);
    setScanningMessage('Enter admin password to access face registration');
  };

  // Function to verify admin password
  const verifyAdminPassword = () => {
    if (password === ADMIN_PASSWORD) {
      setIsRegistrationMode(true);
      setShowPasswordInput(false);
      setUsername(''); // Clear any existing username
      setShowUsernameInput(true); // Immediately show username input
      setScanningMessage('Admin access granted. Please enter a name for the new user.');
    } else {
      setScanningMessage('Incorrect password. Access denied.');
    }
  };

  // Function to register a user's face
  const registerFace = async () => {
    if (!isModelLoaded || !webcamRef.current || !webcamRef.current.video) {
      setScanningMessage('Webcam not ready or models not loaded. Please try again.');
      return;
    }

    // Always show username input in registration mode if no username entered yet
    if (!username.trim()) {
      setShowUsernameInput(true);
      setScanningMessage('Please enter your name to register.');
      return;
    }

    setScanningMessage('Registering your face... Please look at the camera and stay still.');
    setIsLoading(true);
    
    try {
      const video = webcamRef.current.video;
      
      // Make sure video is playing
      if (video.paused || video.ended || !video.readyState === 4) {
        setScanningMessage('Webcam not ready. Please ensure your camera is working.');
        setIsLoading(false);
        return;
      }
      
      const detection = await faceapi.detectSingleFace(video)
        .withFaceLandmarks()
        .withFaceDescriptor();
      
      if (detection) {
        // Store the face descriptor
        const userToStore = {
          name: username.trim(),
          descriptor: detection.descriptor
        };
        setRegisteredUser(userToStore);
        setScanningMessage(`Face registered successfully for ${userToStore.name}! Now you can authenticate.`);
        setShowUsernameInput(false);
        
        // Store in localStorage for session persistence
        try {
          localStorage.setItem('faceDescriptor', JSON.stringify(Array.from(detection.descriptor)));
          localStorage.setItem('faceUsername', userToStore.name);
          console.log('Face descriptor and username saved to local storage');
        } catch (err) {
          console.error('Failed to save face descriptor to local storage:', err);
        }
        
        // Reset registration mode after successful registration
        setIsRegistrationMode(false);
        
      } else {
        setScanningMessage('No face detected. Please ensure your face is clearly visible and try again.');
      }
    } catch (error) {
      console.error('Error during face registration:', error);
      setScanningMessage('Error during registration. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Load saved face on startup
  useEffect(() => {
    try {
      const savedFace = localStorage.getItem('faceDescriptor');
      const savedUsername = localStorage.getItem('faceUsername') || 'User';
      
      if (savedFace) {
        const descriptor = new Float32Array(JSON.parse(savedFace));
        setRegisteredUser({
          name: savedUsername,
          descriptor
        });
        setScanningMessage(`Previously registered face found for ${savedUsername}. You can now authenticate.`);
        console.log('Loaded saved face descriptor from local storage');
      }
    } catch (err) {
      console.error('Error loading saved face:', err);
    }
  }, []);

  // Function to authenticate a user's face
  const authenticateFace = async () => {
    if (!isModelLoaded || !registeredUser || !webcamRef.current || !webcamRef.current.video) {
      if (!registeredUser) {
        setScanningMessage('No registered face found. Please register first.');
      } else {
        setScanningMessage('Webcam not ready or models not loaded. Please try again.');
      }
      return;
    }

    setScanningMessage('Authenticating... Please look at the camera.');
    setIsLoading(true);
    
    try {
      const video = webcamRef.current.video;
      
      // Make sure video is playing
      if (video.paused || video.ended || !video.readyState === 4) {
        setScanningMessage('Webcam not ready. Please ensure your camera is working.');
        setIsLoading(false);
        return;
      }
      
      const detection = await faceapi.detectSingleFace(video)
        .withFaceLandmarks()
        .withFaceDescriptor();
      
      if (detection) {
        // Compare with the registered face
        const labeledDescriptor = new faceapi.LabeledFaceDescriptors(
          registeredUser.name, [registeredUser.descriptor]
        );
        
        const matcher = new faceapi.FaceMatcher(labeledDescriptor, 0.6); // 0.6 is the distance threshold
        const match = matcher.findBestMatch(detection.descriptor);
        
        if (match.label !== 'unknown') {
          setScanningMessage(`Authentication successful! Welcome, ${registeredUser.name}!`);
          setIsAuthenticated(true);
          onAuthenticated(true, registeredUser.name);
        } else {
          setScanningMessage('Authentication failed. Face does not match the registered user.');
        }
      } else {
        setScanningMessage('No face detected. Please ensure your face is clearly visible and try again.');
      }
    } catch (error) {
      console.error('Error during face authentication:', error);
      setScanningMessage('Error during authentication. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Draw face detections on canvas
  useEffect(() => {
    if (!isModelLoaded || !webcamRef.current || !webcamRef.current.video || webcamError) {
      return;
    }

    const video = webcamRef.current.video;
    const canvas = canvasRef.current;
    
    // Set up interval for face detection and drawing
    const interval = setInterval(async () => {
      if (video.readyState === 4) {
        try {
          // Get video dimensions
          const videoWidth = video.videoWidth;
          const videoHeight = video.videoHeight;
          
          // Set canvas dimensions to match video
          canvas.width = videoWidth;
          canvas.height = videoHeight;
          
          // Detect faces
          const detections = await faceapi.detectAllFaces(video)
            .withFaceLandmarks();
          
          // Draw detections on canvas
          const context = canvas.getContext('2d');
          context.clearRect(0, 0, canvas.width, canvas.height);
          faceapi.draw.drawDetections(canvas, detections);
          faceapi.draw.drawFaceLandmarks(canvas, detections);
        } catch (error) {
          console.error('Error in face detection loop:', error);
          // Don't update message here to avoid spamming the user with errors
        }
      }
    }, 200); // Reduced from 100ms to 200ms to lower CPU usage
    
    return () => clearInterval(interval);
  }, [isModelLoaded, webcamError]);

  // Add a manual retry button
  const handleRetryModelLoad = () => {
    setScanningMessage('Retrying model load...');
    setRetryCount(0);
    setIsLoading(true);
  };

  // Function to clear registration
  const clearRegistration = () => {
    try {
      localStorage.removeItem('faceDescriptor');
      localStorage.removeItem('faceUsername');
      setRegisteredUser(null);
      setIsAuthenticated(false);
      setUsername('');
      setScanningMessage('Face registration cleared. You can now register a new face.');
      
      // Notify parent component about authentication status change
      if (onAuthenticated) {
        onAuthenticated(false, null);
      }
    } catch (err) {
      console.error('Error clearing face registration:', err);
      setScanningMessage('Error clearing registration. Please try again.');
    }
  };

  // Reset registration mode when needed
  const cancelRegistration = () => {
    setIsRegistrationMode(false);
    setShowPasswordInput(false);
    setShowUsernameInput(false);
    setScanningMessage('Registration canceled. You can authenticate with existing face or try again.');
  };

  // Return the component JSX
  return (
    <div className="face-auth">
      <h2>Face Authentication</h2>
      <p className={webcamError ? 'error-message' : ''}>{scanningMessage}</p>
      
      {/* Password input for registration */}
      {showPasswordInput && (
        <div className="password-input-container">
          <input
            type="password"
            className="password-input"
            placeholder="Enter admin password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <div className="password-actions">
            <button 
              onClick={verifyAdminPassword}
              className="password-submit-button"
              disabled={!password.trim()}
            >
              Submit
            </button>
            <button 
              onClick={() => setShowPasswordInput(false)}
              className="password-cancel-button"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
      
      {/* Username input - Always visible in registration mode */}
      {isRegistrationMode && (
        <div className="username-input-container">
          <input
            type="text"
            className="username-input"
            placeholder="Enter your name"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
          />
        </div>
      )}
      
      {/* Only show authenticated user badge when actually authenticated */}
      {isAuthenticated && registeredUser && (
        <div className="authenticated-user">
          <span className="user-badge">Logged in as: <strong>{registeredUser.name}</strong></span>
        </div>
      )}
      
      {!webcamError ? (
        <div className="webcam-container" style={{ position: 'relative' }}>
          <Webcam
            ref={webcamRef}
            audio={false}
            width={640}
            height={480}
            screenshotFormat="image/jpeg"
            onUserMediaError={handleWebcamError}
            videoConstraints={{
              width: 640,
              height: 480,
              facingMode: "user"
            }}
          />
          <canvas 
            ref={canvasRef} 
            style={{
              position: 'absolute',
              top: 0,
              left: 0
            }}
          />
          {isLoading && (
            <div className="loading-indicator">
              <div className="spinner"></div>
            </div>
          )}
        </div>
      ) : (
        <div className="webcam-error">
          <p>Camera access is required for face authentication.</p>
          <p>Please enable camera permissions in your browser and refresh the page.</p>
        </div>
      )}
      
      <div className="auth-buttons">
        {!isModelLoaded && retryCount >= 2 && (
          <button 
            onClick={handleRetryModelLoad}
            className="retry-button"
          >
            Retry Loading Models
          </button>
        )}
        
        {/* Only show Register button if we're in registration mode */}
        {isRegistrationMode && (
          <button 
            onClick={registerFace} 
            disabled={!isModelLoaded || isAuthenticated || webcamError || isLoading}
          >
            Register Face
          </button>
        )}
        
        {/* Only show Cancel Registration button if we're in registration mode */}
        {isRegistrationMode && (
          <button 
            onClick={cancelRegistration}
            className="cancel-button"
            disabled={isLoading}
          >
            Cancel Registration
          </button>
        )}
        
        {/* Show Authenticate button when there's a registered user and not authenticated */}
        {registeredUser && !isAuthenticated && (
          <button 
            onClick={authenticateFace} 
            disabled={!isModelLoaded || webcamError || isLoading}
            className="authenticate-button"
          >
            Authenticate
          </button>
        )}
        
        {/* Admin Registration Button - only visible when not in registration mode and not authenticated */}
        {!isRegistrationMode && !isAuthenticated && (
          <button 
            onClick={showAdminPasswordPrompt}
            className="admin-button"
            disabled={isLoading}
          >
            Admin: Register New Face
          </button>
        )}
        
        {/* Clear Registration button - only when there's a registered user */}
        {registeredUser && (
          <button 
            onClick={clearRegistration}
            className="clear-registration-button"
            disabled={isLoading}
          >
            Clear Registration
          </button>
        )}
      </div>
    </div>
  );
};

export default FaceAuth; 